/**
 * Vendor Payment Requests — pure approval workflow.
 *
 * Procurement raises a batch (multiple vendors + amounts + justification +
 * expected revenue). Founder approves. Approved lines land in the disbursal
 * queue where the accountant clicks "Mark paid" (state change only, no journal).
 * When a bank statement debit is later confirmed to that vendor + amount, the
 * line auto-flips to `reconciled` (safety confirmation).
 */
import { useEffect, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, TrashSimple, PaperPlaneTilt, CheckCircle, XCircle, HandCoins, CurrencyInr } from "@phosphor-icons/react";

const STATUS_COLORS = {
    draft: "bg-stone-100 text-stone-700 border-stone-300",
    pending_approval: "bg-amber-100 text-amber-800 border-amber-300",
    approved: "bg-blue-100 text-blue-800 border-blue-300",
    all_paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
    all_reconciled: "bg-teal-100 text-teal-800 border-teal-300",
    rejected: "bg-red-100 text-red-800 border-red-300",
    cancelled: "bg-stone-100 text-stone-500 border-stone-300",
};

const LINE_COLORS = {
    approved: "bg-amber-100 text-amber-800 border-amber-300",
    paid: "bg-blue-100 text-blue-800 border-blue-300",
    reconciled: "bg-emerald-100 text-emerald-800 border-emerald-300",
};


export default function PaymentRequests() {
    const { user } = useAuth();
    const role = user?.role;
    const canCreate = ["founder", "accountant", "purchase"].includes(role);
    const canApprove = role === "founder";
    const canPay = ["founder", "accountant"].includes(role);

    const [requests, setRequests] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [tab, setTab] = useState("mine");
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/payment-requests");
            setRequests(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const loadVendors = async () => {
        try {
            const { data } = await api.get("/ledgers");
            setVendors((data || []).filter((l) => ["vendor", "supplier", "creditor"].includes(l.party_type)));
        } catch (e) { /* silent */ }
    };
    useEffect(() => { load(); loadVendors(); }, []);

    const filtered = requests.filter((r) => {
        if (tab === "mine") return r.requested_by_user_id === user?.id;
        if (tab === "pending") return r.status === "pending_approval";
        if (tab === "disbursal") return r.status === "approved" || r.status === "all_paid";
        if (tab === "reconciled") return r.status === "all_reconciled";
        return true; // "all"
    });

    const submitReq = async (rid) => {
        if (!window.confirm("Submit this request for founder approval?")) return;
        try { await api.post(`/payment-requests/${rid}/submit`); toast.success("Submitted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const cancelReq = async (rid) => {
        if (!window.confirm("Cancel this request?")) return;
        try { await api.post(`/payment-requests/${rid}/cancel`); toast.success("Cancelled"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const deleteReq = async (rid) => {
        if (!window.confirm("Delete this request?")) return;
        try { await api.delete(`/payment-requests/${rid}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const approveReq = async (rid) => {
        if (!window.confirm("Approve this payment request? The lines will move to the accountant's disbursal queue.")) return;
        try { await api.post(`/payment-requests/${rid}/approve`); toast.success("Approved"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const rejectReq = async (rid) => {
        const reason = window.prompt("Reject reason?");
        if (!reason) return;
        try { await api.post(`/payment-requests/${rid}/reject`, { reason }); toast.success("Rejected"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const markPaid = async (rid, lid) => {
        const ref = window.prompt("Payment ref no (optional):") || null;
        try {
            await api.post(`/payment-requests/${rid}/lines/${lid}/mark-paid`, { ref_no: ref });
            toast.success("Marked paid");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const undoPaid = async (rid, lid) => {
        if (!window.confirm("Undo paid marker on this line?")) return;
        try { await api.post(`/payment-requests/${rid}/lines/${lid}/undo-paid`); toast.success("Reverted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Vendor payment requests"
                sub="Procurement raises requests · founder approves · accountant marks paid · bank statement auto-reconciles"
            />
            <div className="p-8 space-y-6">
                {canCreate ? (
                    <div className="flex justify-between items-center">
                        <TabBar tab={tab} setTab={setTab} counts={{
                            mine: requests.filter((r) => r.requested_by_user_id === user?.id).length,
                            pending: requests.filter((r) => r.status === "pending_approval").length,
                            disbursal: requests.filter((r) => r.status === "approved" || r.status === "all_paid").length,
                            reconciled: requests.filter((r) => r.status === "all_reconciled").length,
                            all: requests.length,
                        }} canApprove={canApprove} />
                        <Button onClick={() => { setEditing(null); setShowForm(true); }}
                                style={{ background: "#7c3aed", color: "#fff" }}
                                data-testid="pr-new-btn">
                            <Plus size={16} className="mr-2" /> New request
                        </Button>
                    </div>
                ) : null}

                <RequestList requests={filtered}
                              vendors={vendors}
                              currentUserId={user?.id}
                              canApprove={canApprove}
                              canPay={canPay}
                              onEdit={(r) => { setEditing(r); setShowForm(true); }}
                              onSubmit={submitReq}
                              onCancel={cancelReq}
                              onDelete={deleteReq}
                              onApprove={approveReq}
                              onReject={rejectReq}
                              onMarkPaid={markPaid}
                              onUndoPaid={undoPaid} />
            </div>

            {showForm ? (
                <RequestFormDialog vendors={vendors}
                                    editing={editing}
                                    onClose={() => { setShowForm(false); setEditing(null); }}
                                    onSaved={() => { setShowForm(false); setEditing(null); load(); }} />
            ) : null}
        </>
    );
}


function TabBar({ tab, setTab, counts, canApprove }) {
    const tabs = [
        { id: "mine",       label: "My requests",       count: counts.mine },
        { id: "pending",    label: "Pending approval",  count: counts.pending, gated: canApprove },
        { id: "disbursal",  label: "Disbursal queue",   count: counts.disbursal },
        { id: "reconciled", label: "Reconciled",        count: counts.reconciled },
        { id: "all",        label: "All",               count: counts.all },
    ];
    return (
        <div className="flex items-center gap-2 border-b" style={{ borderColor: "var(--se-border)" }}>
            {tabs.map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)}
                        className="px-3 py-2 text-sm border-b-2 transition-all"
                        style={{ borderColor: tab === t.id ? "#7c3aed" : "transparent",
                                 color: tab === t.id ? "#7c3aed" : "#57534e" }}
                        data-testid={`pr-tab-${t.id}`}>
                    {t.label} <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-stone-100">{t.count}</span>
                </button>
            ))}
        </div>
    );
}


function RequestList({ requests, vendors, currentUserId, canApprove, canPay, onEdit, onSubmit, onCancel, onDelete, onApprove, onReject, onMarkPaid, onUndoPaid }) {
    if (!requests.length) {
        return <div className="text-sm text-stone-500 text-center py-16 border rounded-md bg-white"
                    style={{ borderColor: "var(--se-border)" }}>
            No requests here yet.
        </div>;
    }
    return (
        <div className="space-y-4">
            {requests.map((r) => (
                <RequestCard key={r.id} r={r}
                              vendors={vendors}
                              isOwner={r.requested_by_user_id === currentUserId}
                              canApprove={canApprove}
                              canPay={canPay}
                              onEdit={onEdit}
                              onSubmit={onSubmit}
                              onCancel={onCancel}
                              onDelete={onDelete}
                              onApprove={onApprove}
                              onReject={onReject}
                              onMarkPaid={onMarkPaid}
                              onUndoPaid={onUndoPaid} />
            ))}
        </div>
    );
}


function RequestCard({ r, isOwner, canApprove, canPay, onEdit, onSubmit, onCancel, onDelete, onApprove, onReject, onMarkPaid, onUndoPaid }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border rounded-md bg-white" style={{ borderColor: "var(--se-border)" }}
             data-testid={`pr-card-${r.id}`}>
            <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3"
                 style={{ borderColor: "var(--se-border)" }}>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="font-mono text-sm">{r.request_no}</div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-sm border ${STATUS_COLORS[r.status] || STATUS_COLORS.draft}`}>
                        {r.status.replace(/_/g, " ")}
                    </span>
                    <div className="text-[11px] text-stone-500">
                        by <b>{r.requested_by_name}</b> ({r.requested_by_role}) · {r.requested_at?.slice(0, 10)} · {r.lines.length} line{r.lines.length === 1 ? "" : "s"}
                    </div>
                </div>
                <div className="flex items-center gap-4 flex-wrap text-right">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-stone-500">Total ask</div>
                        <div className="font-serif text-lg">₹ {inr(r.total_amount || 0)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-stone-500">Expected revenue</div>
                        <div className="font-serif text-lg text-emerald-700">₹ {inr(r.expected_revenue_total || 0)}</div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setOpen(!open)}
                            data-testid={`pr-toggle-${r.id}`}>
                        {open ? "Hide" : "Details"}
                    </Button>
                </div>
            </div>
            {open ? (
                <div className="p-4 space-y-4">
                    {r.rejection_reason ? (
                        <div className="border-l-4 border-red-400 bg-red-50 p-3 text-[12px]">
                            <b>Rejection reason:</b> {r.rejection_reason}
                        </div>
                    ) : null}
                    {r.note ? (
                        <div className="text-[11px] text-stone-500 italic">Note: {r.note}</div>
                    ) : null}
                    <div className="overflow-auto">
                        <table className="se-table text-sm w-full" data-testid={`pr-lines-${r.id}`}>
                            <thead><tr>
                                <th>Vendor</th>
                                <th className="text-right">Amount</th>
                                <th>Description</th>
                                <th className="text-right">Expected revenue</th>
                                <th>Status</th>
                                <th className="text-right"></th>
                            </tr></thead>
                            <tbody>
                                {r.lines.map((ln) => (
                                    <tr key={ln.id}>
                                        <td className="text-[11px]">{ln.vendor_name}</td>
                                        <td className="se-num text-right"><b>{inr(ln.amount || 0)}</b></td>
                                        <td className="text-[11px]">{ln.description}</td>
                                        <td className="se-num text-right text-emerald-700">{inr(ln.expected_revenue || 0)}</td>
                                        <td>
                                            <span className={`text-[10px] px-2 py-0.5 rounded-sm border ${LINE_COLORS[ln.status] || LINE_COLORS.approved}`}>
                                                {ln.status}
                                            </span>
                                            {ln.paid_ref_no ? <div className="text-[10px] text-stone-500 mt-0.5">ref {ln.paid_ref_no}</div> : null}
                                        </td>
                                        <td className="text-right">
                                            {r.status === "approved" && canPay ? (
                                                ln.status === "approved" ? (
                                                    <Button size="sm"
                                                            onClick={() => onMarkPaid(r.id, ln.id)}
                                                            style={{ background: "#7c3aed", color: "#fff" }}
                                                            data-testid={`pr-mark-paid-${ln.id}`}>
                                                        Mark paid
                                                    </Button>
                                                ) : ln.status === "paid" ? (
                                                    <Button size="sm" variant="outline"
                                                            onClick={() => onUndoPaid(r.id, ln.id)}
                                                            data-testid={`pr-undo-paid-${ln.id}`}>
                                                        Undo
                                                    </Button>
                                                ) : (
                                                    <span className="text-[10px] text-emerald-700">
                                                        <CheckCircle size={10} weight="fill" className="inline mr-1" />
                                                        bank matched
                                                    </span>
                                                )
                                            ) : null}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex gap-2 justify-end flex-wrap">
                        {(r.status === "draft" || r.status === "rejected") && isOwner ? (
                            <>
                                <Button size="sm" variant="outline" onClick={() => onEdit(r)}
                                        data-testid={`pr-edit-${r.id}`}>Edit</Button>
                                <Button size="sm" variant="outline" onClick={() => onDelete(r.id)}
                                        style={{ color: "#b91c1c", borderColor: "#fecaca" }}
                                        data-testid={`pr-delete-${r.id}`}>
                                    <TrashSimple size={12} className="mr-1" /> Delete
                                </Button>
                                <Button size="sm" onClick={() => onSubmit(r.id)}
                                        style={{ background: "#7c3aed", color: "#fff" }}
                                        data-testid={`pr-submit-${r.id}`}>
                                    <PaperPlaneTilt size={12} className="mr-1" /> Submit for approval
                                </Button>
                            </>
                        ) : null}
                        {r.status === "pending_approval" && isOwner ? (
                            <Button size="sm" variant="outline" onClick={() => onCancel(r.id)}
                                    data-testid={`pr-cancel-${r.id}`}>Cancel</Button>
                        ) : null}
                        {r.status === "pending_approval" && canApprove ? (
                            <>
                                <Button size="sm" variant="outline" onClick={() => onReject(r.id)}
                                        style={{ color: "#b91c1c", borderColor: "#fecaca" }}
                                        data-testid={`pr-reject-${r.id}`}>
                                    <XCircle size={12} className="mr-1" /> Reject
                                </Button>
                                <Button size="sm" onClick={() => onApprove(r.id)}
                                        style={{ background: "#059669", color: "#fff" }}
                                        data-testid={`pr-approve-${r.id}`}>
                                    <CheckCircle size={12} className="mr-1" /> Approve
                                </Button>
                            </>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}


function RequestFormDialog({ vendors, editing, onClose, onSaved }) {
    const [lines, setLines] = useState(() =>
        editing ? editing.lines.map((l) => ({
            vendor_ledger_id: l.vendor_ledger_id,
            amount: l.amount,
            description: l.description,
            expected_revenue: l.expected_revenue || 0,
        })) : [{ vendor_ledger_id: "", amount: "", description: "", expected_revenue: "" }]
    );
    const [note, setNote] = useState(editing?.note || "");
    const [saving, setSaving] = useState(false);

    const updateLine = (i, field, val) => {
        const next = [...lines];
        next[i] = { ...next[i], [field]: val };
        setLines(next);
    };
    const addLine = () => setLines([...lines, { vendor_ledger_id: "", amount: "", description: "", expected_revenue: "" }]);
    const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

    const submit = async (thenSubmit = false) => {
        for (const [i, l] of lines.entries()) {
            if (!l.vendor_ledger_id) return toast.error(`Line ${i + 1}: pick a vendor`);
            if (!l.amount || Number(l.amount) <= 0) return toast.error(`Line ${i + 1}: enter amount`);
            if (!l.description?.trim()) return toast.error(`Line ${i + 1}: add justification`);
        }
        setSaving(true);
        try {
            const payload = {
                lines: lines.map((l) => ({
                    vendor_ledger_id: l.vendor_ledger_id,
                    amount: Number(l.amount),
                    description: l.description.trim(),
                    expected_revenue: Number(l.expected_revenue || 0),
                })),
                note: note.trim() || null,
            };
            const { data } = editing
                ? await api.patch(`/payment-requests/${editing.id}`, payload)
                : await api.post("/payment-requests", payload);
            if (thenSubmit) {
                await api.post(`/payment-requests/${data.id}/submit`);
                toast.success("Submitted for approval");
            } else {
                toast.success("Draft saved");
            }
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const total = lines.reduce((s, l) => s + Number(l.amount || 0), 0);
    const expRev = lines.reduce((s, l) => s + Number(l.expected_revenue || 0), 0);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto p-4"
             style={{ background: "rgba(0,0,0,0.35)" }}
             data-testid="pr-form-dialog">
            <div className="bg-white rounded-md border shadow-lg w-full max-w-4xl"
                 style={{ borderColor: "var(--se-border)" }}>
                <div className="px-5 py-4 border-b" style={{ borderColor: "var(--se-border)" }}>
                    <div className="text-lg font-serif">{editing ? `Edit ${editing.request_no}` : "New vendor payment request"}</div>
                    <div className="text-[11px] text-stone-500 mt-1">
                        Add one line per vendor. Justification and expected revenue help the founder decide fast.
                    </div>
                </div>
                <div className="p-5 space-y-4">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-widest text-stone-500">
                            <tr>
                                <th className="text-left pb-2">Vendor</th>
                                <th className="text-right pb-2 w-28">Amount</th>
                                <th className="text-left pb-2">Justification</th>
                                <th className="text-right pb-2 w-32">Expected revenue</th>
                                <th className="w-8"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((l, i) => (
                                <tr key={i}>
                                    <td className="pr-2 pb-2">
                                        <select value={l.vendor_ledger_id}
                                                onChange={(e) => updateLine(i, "vendor_ledger_id", e.target.value)}
                                                className="border rounded-sm px-2 py-1.5 text-sm w-full"
                                                data-testid={`pr-line-vendor-${i}`}>
                                            <option value="">— pick vendor —</option>
                                            {vendors.map((v) => (
                                                <option key={v.id} value={v.id}>{v.name} {v.gstin ? `· ${v.gstin}` : ""}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="pr-2 pb-2">
                                        <input type="number" step="0.01" min="0"
                                                value={l.amount}
                                                onChange={(e) => updateLine(i, "amount", e.target.value)}
                                                className="border rounded-sm px-2 py-1.5 text-sm w-full text-right"
                                                placeholder="0.00"
                                                data-testid={`pr-line-amount-${i}`} />
                                    </td>
                                    <td className="pr-2 pb-2">
                                        <input type="text" value={l.description}
                                                onChange={(e) => updateLine(i, "description", e.target.value)}
                                                className="border rounded-sm px-2 py-1.5 text-sm w-full"
                                                placeholder="Why we need to pay this"
                                                data-testid={`pr-line-desc-${i}`} />
                                    </td>
                                    <td className="pr-2 pb-2">
                                        <input type="number" step="0.01" min="0"
                                                value={l.expected_revenue}
                                                onChange={(e) => updateLine(i, "expected_revenue", e.target.value)}
                                                className="border rounded-sm px-2 py-1.5 text-sm w-full text-right"
                                                placeholder="Revenue this unlocks"
                                                data-testid={`pr-line-rev-${i}`} />
                                    </td>
                                    <td className="pb-2">
                                        <button onClick={() => removeLine(i)}
                                                disabled={lines.length === 1}
                                                className="text-stone-500 hover:text-red-600 disabled:opacity-30"
                                                data-testid={`pr-line-remove-${i}`}>
                                            <TrashSimple size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <Button size="sm" variant="outline" onClick={addLine}
                            data-testid="pr-add-line-btn">
                        <Plus size={12} className="mr-1" /> Add another vendor
                    </Button>

                    <div className="grid grid-cols-2 gap-4 pt-4 border-t"
                         style={{ borderColor: "var(--se-border)" }}>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-stone-500">Overall note (optional)</label>
                            <textarea value={note} onChange={(e) => setNote(e.target.value)}
                                    className="border rounded-sm px-2 py-1.5 text-sm w-full mt-1"
                                    rows={2}
                                    data-testid="pr-note" />
                        </div>
                        <div className="text-right space-y-1">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-stone-500">Total ask</div>
                                <div className="font-serif text-2xl">₹ {inr(total)}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-stone-500">Expected revenue</div>
                                <div className="font-serif text-lg text-emerald-700">₹ {inr(expRev)}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="px-5 py-3 border-t flex justify-end gap-2"
                     style={{ borderColor: "var(--se-border)" }}>
                    <Button variant="outline" onClick={onClose} data-testid="pr-form-cancel">Cancel</Button>
                    <Button variant="outline" onClick={() => submit(false)} disabled={saving}
                            data-testid="pr-save-draft">Save draft</Button>
                    <Button onClick={() => submit(true)} disabled={saving}
                            style={{ background: "#7c3aed", color: "#fff" }}
                            data-testid="pr-save-submit">
                        <PaperPlaneTilt size={12} className="mr-2" />
                        {saving ? "Saving…" : "Save & submit for approval"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
